(() => {
  const initialized = new WeakSet();

  const initialize = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);

    const viewport = root.querySelector('[data-details-viewport]');
    const slides = [...root.querySelectorAll('[data-details-slide]')];
    const previous = root.querySelector('[data-details-prev]');
    const next = root.querySelector('[data-details-next]');
    const current = root.querySelector('[data-details-current]');
    const progress = root.querySelector('[data-details-progress]');
    const sticky = root.querySelector('[data-details-sticky]');

    if (!viewport || !slides.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktopSequence = window.matchMedia('(min-width: 990px)');
    const sequenceRequested = root.dataset.scrollSequence === 'true';
    let activeIndex = 0;
    let frame = 0;
    let dragFrame = 0;
    let settleFrame = 0;
    let dragging = false;
    let settling = false;
    let startX = 0;
    let startScrollLeft = 0;
    let startIndex = 0;
    let movedDistance = 0;
    let pendingScrollLeft = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    let scrollSequenceActive = false;
    let sequenceFrame = 0;
    let sequenceTarget = 0;

    const slideLeft = (slide) => slide.offsetLeft - slides[0].offsetLeft;

    const update = (index) => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((slide, slideIndex) => {
        if (slideIndex === activeIndex) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      if (progress) progress.style.transform = `scaleX(${((activeIndex + 1) / slides.length).toFixed(4)})`;
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === slides.length - 1;
    };

    const nearestIndex = (position = viewport.scrollLeft) => {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const distance = Math.abs(position - slideLeft(slide));
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    };

    const sync = () => {
      frame = 0;
      if (dragging || settling) return;
      update(nearestIndex());
    };

    const scheduleSync = () => {
      if (dragging || settling || scrollSequenceActive) return;
      if (!frame) frame = window.requestAnimationFrame(sync);
    };

    const stopSettle = () => {
      if (settleFrame) window.cancelAnimationFrame(settleFrame);
      settleFrame = 0;
      settling = false;
      viewport.classList.remove('is-settling');
    };

    const goTo = (index, smooth = true) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      const targetLeft = slideLeft(slides[targetIndex]);
      const from = viewport.scrollLeft;
      const distance = targetLeft - from;

      stopSettle();
      update(targetIndex);

      if (!smooth || reducedMotion.matches || Math.abs(distance) < 1) {
        viewport.scrollLeft = targetLeft;
        return;
      }

      settling = true;
      viewport.classList.add('is-settling');
      const startedAt = performance.now();
      const duration = Math.min(520, Math.max(340, Math.abs(distance) * 0.32));

      const animate = (now) => {
        const progressValue = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progressValue, 3);
        viewport.scrollLeft = from + distance * eased;

        if (progressValue < 1) {
          settleFrame = window.requestAnimationFrame(animate);
          return;
        }

        viewport.scrollLeft = targetLeft;
        settleFrame = 0;
        settling = false;
        viewport.classList.remove('is-settling');
        update(targetIndex);
      };

      settleFrame = window.requestAnimationFrame(animate);
    };

    const headerOffset = () => {
      const value = window.getComputedStyle(document.documentElement).getPropertyValue('--ls-header-offset');
      return Number.parseFloat(value) || 0;
    };

    const sequenceTravel = () => Math.max(1, root.offsetHeight - (sticky?.offsetHeight || window.innerHeight));

    const renderSequence = () => {
      sequenceFrame = 0;
      if (!scrollSequenceActive) return;
      viewport.scrollLeft = sequenceTarget;
      update(nearestIndex(sequenceTarget));
    };

    const syncSequenceToPage = () => {
      if (!scrollSequenceActive) return;
      const rect = root.getBoundingClientRect();
      const progressValue = Math.max(0, Math.min(1, (headerOffset() - rect.top) / sequenceTravel()));
      sequenceTarget = slideLeft(slides[slides.length - 1]) * progressValue;
      if (!sequenceFrame) sequenceFrame = window.requestAnimationFrame(renderSequence);
    };

    const scrollToStage = (index) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      if (!scrollSequenceActive || slides.length < 2) {
        goTo(targetIndex);
        return;
      }

      const sectionTop = window.scrollY + root.getBoundingClientRect().top - headerOffset();
      const targetTop = sectionTop + (sequenceTravel() * (targetIndex / (slides.length - 1)));
      window.scrollTo({ top: targetTop, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    const setSequenceMode = () => {
      const shouldActivate = sequenceRequested && desktopSequence.matches && slides.length > 1;
      if (shouldActivate === scrollSequenceActive) {
        if (shouldActivate) syncSequenceToPage();
        return;
      }

      scrollSequenceActive = shouldActivate;
      root.classList.toggle('is-scroll-active', scrollSequenceActive);
      stopSettle();

      if (scrollSequenceActive) {
        syncSequenceToPage();
      } else {
        if (sequenceFrame) window.cancelAnimationFrame(sequenceFrame);
        sequenceFrame = 0;
        goTo(nearestIndex(), false);
      }
    };

    previous?.addEventListener('click', () => scrollToStage(activeIndex - 1));
    next?.addEventListener('click', () => scrollToStage(activeIndex + 1));

    viewport.addEventListener('scroll', scheduleSync, { passive: true });
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollToStage(activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollToStage(activeIndex + 1);
      }
    });

    viewport.addEventListener('pointerdown', (event) => {
      if (scrollSequenceActive) return;
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      event.preventDefault();
      stopSettle();
      dragging = true;
      startX = event.clientX;
      startScrollLeft = viewport.scrollLeft;
      startIndex = nearestIndex();
      movedDistance = 0;
      pendingScrollLeft = startScrollLeft;
      lastX = event.clientX;
      lastTime = performance.now();
      velocity = 0;
      viewport.classList.add('is-dragging');
      viewport.setPointerCapture(event.pointerId);
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      event.preventDefault();
      const movement = event.clientX - startX;
      const now = performance.now();
      const elapsed = Math.max(1, now - lastTime);
      const instantVelocity = (event.clientX - lastX) / elapsed;
      velocity = (velocity * 0.72) + (instantVelocity * 0.28);
      lastX = event.clientX;
      lastTime = now;
      movedDistance = Math.max(movedDistance, Math.abs(movement));
      pendingScrollLeft = Math.max(0, Math.min(viewport.scrollWidth - viewport.clientWidth, startScrollLeft - movement));

      if (!dragFrame) {
        dragFrame = window.requestAnimationFrame(() => {
          viewport.scrollLeft = pendingScrollLeft;
          dragFrame = 0;
        });
      }
    });

    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      if (dragFrame) {
        window.cancelAnimationFrame(dragFrame);
        dragFrame = 0;
        viewport.scrollLeft = pendingScrollLeft;
      }
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);

      const movement = event.clientX - startX;
      const threshold = Math.min(72, viewport.clientWidth * 0.055);
      let targetIndex = startIndex;

      if (Math.abs(movement) >= threshold || Math.abs(velocity) > 0.35) {
        const direction = movement < 0 || velocity < 0 ? 1 : -1;
        targetIndex = startIndex + direction;
      }

      goTo(targetIndex);
    };

    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    viewport.addEventListener('dragstart', (event) => event.preventDefault());
    viewport.addEventListener('click', (event) => {
      if (movedDistance > 8) event.preventDefault();
      movedDistance = 0;
    }, true);

    window.addEventListener('scroll', syncSequenceToPage, { passive: true });
    window.addEventListener('resize', setSequenceMode, { passive: true });
    desktopSequence.addEventListener?.('change', setSequenceMode);
    update(0);
    setSequenceMode();
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-lusso-details-slider]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
